import { useEffect, useState } from "react";
import {
    Smartphone,
    Download,
    Apple,
    X,
    Bell,
    Settings,
    CheckCircle2,
    LogIn,
    Info,
    type LucideIcon,
} from "lucide-react";

const APK_URL = `${import.meta.env.BASE_URL}downloads/rizmir-santiye.apk`;
const APK_SIZE = "~113 MB";

interface InstallStep {
    icon: LucideIcon;
    text: string;
}

const INSTALL_STEPS: InstallStep[] = [
    { icon: Download, text: '"APK\'yı İndir" butonuna dokunun' },
    {
        icon: Bell,
        text: "İndirme tamamlanınca bildirime dokunun (veya İndirilenler'den açın)",
    },
    {
        icon: Settings,
        text: '"Bilinmeyen kaynaklardan yükle" uyarısı çıkarsa Ayarlar\'dan izin verin',
    },
    { icon: CheckCircle2, text: '"Yükle"ye dokunun' },
    {
        icon: LogIn,
        text: "Uygulamayı açın ve şirket hesabınızla giriş yapın",
    },
];

export function AppDownloadSection() {
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        if (!modalOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setModalOpen(false);
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [modalOpen]);

    return (
        <>
            <section
                data-testid="app-download-section"
                className="relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 p-5 shadow-sm sm:p-6"
                aria-label="Mobil uygulamayı indir"
            >
                {/* Dekoratif arka plan desenleri */}
                <div
                    className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10"
                    aria-hidden="true"
                />
                <div
                    className="pointer-events-none absolute -bottom-12 right-24 h-28 w-28 rounded-full bg-white/5"
                    aria-hidden="true"
                />

                <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25">
                            <Smartphone
                                className="h-6 w-6 text-white"
                                aria-hidden="true"
                            />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">
                                Rizmir Şantiye Cebinizde
                            </h2>
                            <p className="mt-1 max-w-md text-sm text-blue-100">
                                Saha ekipleri için mobil uygulama — görevler,
                                mesajlaşma, aramalar cebinizde.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 sm:shrink-0 sm:flex-row sm:items-center">
                        <button
                            type="button"
                            data-testid="app-download-android-button"
                            onClick={() => setModalOpen(true)}
                            className="flex items-center justify-center gap-3 rounded-xl bg-white px-4 py-2.5 text-left shadow-md transition hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
                        >
                            <Download
                                className="h-5 w-5 text-blue-600"
                                aria-hidden="true"
                            />
                            <span className="leading-tight">
                                <span className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">
                                    Android
                                </span>
                                <span className="block text-sm font-semibold text-slate-900">
                                    APK İndir
                                </span>
                            </span>
                        </button>

                        <div className="relative">
                            <span className="absolute -right-2 -top-2 z-10 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-950 shadow">
                                Yakında
                            </span>
                            <button
                                type="button"
                                data-testid="app-download-ios-button"
                                disabled
                                aria-disabled="true"
                                className="flex w-full cursor-not-allowed items-center justify-center gap-3 rounded-xl border border-white/30 bg-white/10 px-4 py-2.5 text-left opacity-60 sm:w-auto"
                            >
                                <Apple
                                    className="h-5 w-5 text-white"
                                    aria-hidden="true"
                                />
                                <span className="leading-tight">
                                    <span className="block text-[10px] font-medium uppercase tracking-wide text-blue-100">
                                        iOS
                                    </span>
                                    <span className="block text-sm font-semibold text-white">
                                        App Store
                                    </span>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {modalOpen && (
                <div
                    className="fixed inset-0 z-[110] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
                    role="presentation"
                    onClick={() => setModalOpen(false)}
                >
                    <div
                        data-testid="app-download-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="app-download-modal-title"
                        className="flex max-h-[90vh] w-full flex-col rounded-t-2xl bg-white shadow-2xl sm:max-w-lg sm:rounded-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sm:px-5">
                            <div className="flex items-center gap-2 text-slate-800">
                                <Smartphone
                                    className="h-5 w-5 text-blue-600"
                                    aria-hidden="true"
                                />
                                <h2
                                    id="app-download-modal-title"
                                    className="text-lg font-semibold"
                                >
                                    Android Uygulamasını Kurun
                                </h2>
                            </div>
                            <button
                                type="button"
                                onClick={() => setModalOpen(false)}
                                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                                aria-label="Kapat"
                            >
                                <X className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
                            <a
                                href={APK_URL}
                                download
                                data-testid="app-download-apk-button"
                                className="flex items-center justify-center gap-3 rounded-xl bg-blue-600 px-6 py-3.5 text-white shadow-md transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                                <Download
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                />
                                <span className="text-base font-semibold">
                                    APK'yı İndir
                                </span>
                                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium">
                                    {APK_SIZE}
                                </span>
                            </a>

                            <h3 className="mb-3 mt-6 text-sm font-semibold text-slate-900">
                                Kurulum Adımları
                            </h3>
                            <ol className="space-y-3">
                                {INSTALL_STEPS.map((step, index) => {
                                    const StepIcon = step.icon;
                                    return (
                                        <li
                                            key={index}
                                            className="flex items-start gap-3"
                                        >
                                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                                                <StepIcon
                                                    className="h-4 w-4"
                                                    aria-hidden="true"
                                                />
                                            </span>
                                            <p className="pt-1 text-sm text-slate-700">
                                                <span className="font-semibold text-slate-900">
                                                    {index + 1}.
                                                </span>{" "}
                                                {step.text}
                                            </p>
                                        </li>
                                    );
                                })}
                            </ol>

                            <div className="mt-6 flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                                <Info
                                    className="mt-0.5 h-4 w-4 shrink-0 text-slate-400"
                                    aria-hidden="true"
                                />
                                <p>
                                    Uygulama doğrudan resmi Rizmir sunucusundan
                                    indirilir. Tarayıcınızın "zararlı olabilir"
                                    uyarısı Google Play dışı yüklemelerde
                                    normaldir.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
